import { Controller } from "@hotwired/stimulus"

// Non-modal bottom-right onboarding dialog. Owns the entrance/exit animation and
// posts interactions in the background (no page dimming, no reload). Two states:
// an expanded checklist panel and a collapsed launcher pill.
//
//   dismiss (×) -> animate the panel out, reveal the launcher, snooze on the server
//   expand      -> animate the panel back in, record an "expand" analytics event
//   skip        -> animate out and remove, skip on the server (permanent)
//   copyShareLink -> copy the storefront URL, acknowledge the manual step
export default class extends Controller {
  static targets = ["panel", "launcher", "progress", "shareButton", "celebration"]
  static values = {
    autoExpand: Boolean,
    expandRecorded: Boolean,
    othersComplete: Boolean,
    total: Number,
    shareLink: String,
    snoozeUrl: String,
    skipUrl: String,
    shareUrl: String,
    eventUrl: String
  }

  // Classes that hold an element off-screen/invisible for the transition.
  static HIDDEN = ["opacity-0", "translate-y-4", "scale-95", "pointer-events-none"]

  connect() {
    // Both states are absolutely stacked in the same bottom-right corner and start
    // off-screen (set in the markup). Animate the active one in on the next frame so
    // the entrance is visible ("pops out" from the corner).
    requestAnimationFrame(() => {
      if (this.autoExpandValue) {
        this.#onscreen(this.panelTarget)
        this.#recordExpand()
      } else {
        this.#onscreen(this.launcherTarget)
      }
    })
  }

  expand() {
    this.#offscreen(this.launcherTarget)
    this.#onscreen(this.panelTarget)
    this.#recordExpand()
  }

  dismiss() {
    this.#offscreen(this.panelTarget)
    this.#onscreen(this.launcherTarget)
    this.#post(this.snoozeUrlValue)
  }

  skip() {
    this.#offscreen(this.panelTarget)
    this.#offscreen(this.launcherTarget)
    setTimeout(() => this.element.remove(), 300)
    this.#post(this.skipUrlValue)
  }

  // A step CTA was clicked — record it for analytics before navigation proceeds.
  stepClick(event) {
    const step = event.params?.step || event.currentTarget.dataset.onboardingGuideStepParam
    this.#post(this.eventUrlValue, { event: "step_click", step })
  }

  async copyShareLink() {
    if (this.acknowledged) return // idempotent — a second click must not re-count
    this.acknowledged = true
    try {
      await navigator.clipboard.writeText(this.shareLinkValue)
    } catch (_e) {
      // Clipboard may be blocked (permissions/HTTP) — still acknowledge the step.
    }
    if (this.hasShareButtonTarget) {
      this.shareButtonTarget.textContent = "Copied ✓"
      this.shareButtonTarget.disabled = true
    }
    await this.#post(this.shareUrlValue)
    this.#bumpProgress()
    if (this.othersCompleteValue && this.hasCelebrationTarget) {
      this.celebrationTarget.classList.remove("hidden")
    }
  }

  // ── helpers ──────────────────────────────────────────────────────────────
  // Slide/scale an element out (invisible, non-interactive) or back in. Both the
  // panel and launcher are absolutely stacked, so this cross-fades between them.
  #offscreen(el) {
    el.classList.add(...this.constructor.HIDDEN)
  }

  #onscreen(el) {
    el.classList.remove(...this.constructor.HIDDEN)
  }

  #recordExpand() {
    if (this.expandRecordedValue) return
    this.expandRecordedValue = true
    this.#post(this.eventUrlValue, { event: "expand" })
  }

  #bumpProgress() {
    if (!this.hasProgressTarget) return
    const current = parseInt(this.progressTarget.textContent, 10)
    if (Number.isNaN(current)) return
    // Clamp so the count can never read past the total (e.g. "5/4").
    this.progressTarget.textContent = Math.min(current + 1, this.totalValue)
  }

  #post(url, body = {}) {
    const params = new URLSearchParams(body)
    return fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "text/html"
      },
      body: params.toString(),
      credentials: "same-origin",
      keepalive: true
    }).catch(() => {})
  }
}
